import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, Button } from "@learn-me/ui";
import { FadeIn } from "@/components/animated";
import {
  OFFERING_CATEGORY_LABELS,
  OFFERING_CTA_LABELS,
  OFFERING_FOCUS_LABELS,
} from "@learn-me/types";
import { offeringsApi } from "@/features/offerings/api/offeringsApi";
import { OfferingRequestForm } from "@/features/offerings/components/OfferingRequestForm";
import { ArrowLeft, Clock, MapPin, Loader2 } from "lucide-react";

export default function OfferingDetailPage() {
  const { slug } = useParams<{ slug: string }>();

  const {
    data: offering,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["offering", slug],
    queryFn: () => offeringsApi.getBySlug(slug!),
    enabled: !!slug,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="container py-32 flex justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (isError || !offering) {
    return (
      <div className="container py-24 text-center">
        <h1 className="font-display text-2xl font-bold text-secondary-900 mb-3">
          No encontramos este programa
        </h1>
        <p className="text-secondary-600 mb-8">
          Puede que haya cambiado de dirección o que ya no esté disponible.
        </p>
        <Link to="/programas">
          <Button variant="outline">Ver todos los programas</Button>
        </Link>
      </div>
    );
  }

  const backTo =
    offering.category === "CAPACITACION"
      ? "/programas?tipo=capacitaciones"
      : "/programas?tipo=programas";

  return (
    <div className="bg-white">
      <section className="bg-gradient-to-b from-secondary-50 to-white border-b">
        <div className="container py-12 md:py-16">
          <Link
            to={backTo}
            className="inline-flex items-center gap-2 text-sm text-secondary-600 hover:text-secondary-900 mb-8"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver a{" "}
            {offering.category === "CAPACITACION"
              ? "capacitaciones"
              : "programas"}
          </Link>

          <FadeIn>
            <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full mb-4">
              {OFFERING_CATEGORY_LABELS[offering.category]}
            </span>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-secondary-900 max-w-4xl">
              {offering.title}
            </h1>
          </FadeIn>
        </div>
      </section>

      <div className="container py-12">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-10">
            <p className="text-lg text-secondary-700 leading-relaxed">
              {offering.summary}
            </p>

            <div>
              <h2 className="font-display text-xl font-bold text-secondary-900 mb-4">
                {OFFERING_FOCUS_LABELS[offering.category]}
              </h2>
              <div className="flex flex-wrap gap-2">
                {offering.focusAreas.map((area) => (
                  <span
                    key={area}
                    className="px-3 py-1.5 bg-primary-50 text-primary-700 text-sm rounded-full"
                  >
                    {area}
                  </span>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <Card>
                <CardContent className="p-5">
                  <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-secondary-500 mb-2">
                    <Clock className="h-4 w-4" />
                    Duración
                  </p>
                  <p className="text-secondary-900 font-medium">
                    {offering.durationLabel}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-5">
                  <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-secondary-500 mb-2">
                    <MapPin className="h-4 w-4" />
                    Modalidad
                  </p>
                  <p className="text-secondary-900 font-medium">
                    {offering.modalities.join(" · ")}
                  </p>
                </CardContent>
              </Card>
            </div>

            <p className="text-secondary-600 leading-relaxed">
              Los contenidos, la duración y la metodología se ajustan a los
              objetivos y a las características de la población de su
              organización.
            </p>
          </div>

          <div>
            <Card className="lg:sticky lg:top-24">
              <CardContent className="p-6">
                <h2 className="font-display text-xl font-bold text-secondary-900 mb-2">
                  {OFFERING_CTA_LABELS[offering.category]}
                </h2>
                <p className="text-sm text-secondary-600 mb-6">
                  Compártanos sus datos y le enviaremos una propuesta adaptada a
                  su organización.
                </p>
                <OfferingRequestForm
                  offeringId={offering.id}
                  contextLabel={offering.title}
                  submitLabel={OFFERING_CTA_LABELS[offering.category]}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
